import { useState, useRef, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

export function useGoogleStt() {
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const [waveform, setWaveform] = useState<number[]>(Array(32).fill(0));

  const mediaRef = useRef<MediaRecorder>();
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext>();
  const analyserRef = useRef<AnalyserNode>();
  const animRef = useRef<number>();

  const { toast } = useToast();

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve((reader.result as string).split(',')[1]);
      };
      reader.readAsDataURL(blob);
    });
  };

  const start = async () => {
    console.log('[useGoogleStt] start invoked, listening=', listening);
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      console.error('[useGoogleStt] getUserMedia or MediaRecorder unsupported');
      toast({ title: '오류', description: '브라우저가 음성인식을 지원하지 않습니다.' });
      return;
    }
    if (listening) return;

    // reset
    setText('');
    setWaveform(Array(32).fill(0));
    chunksRef.current = [];

    // show recording UI immediately
    setListening(true);
    

    try {
      // get mic
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // set up analyser for waveform
      const ctx = new AudioContext({ sampleRate: 48000 });
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyserRef.current = analyser;
      source.connect(analyser);

      // media recorder
      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });
      mediaRef.current = recorder;

      const transcribeChunk = async (blob: Blob) => {
        try {
          // accumulate chunks and transcribe full audio so far
          chunksRef.current.push(blob);
          const fullBlob = new Blob(chunksRef.current, { type: blob.type });
          const base64 = await blobToBase64(fullBlob);
          // determine STT endpoint with local dev fallback
          let endpoint = import.meta.env.VITE_STT_FUNCTION_URL;
          // during local development, prefer local server
          if (!endpoint && import.meta.env.DEV) {
            endpoint = 'http://localhost:3001/speechToText';
          }
          // fallback to Supabase Edge Function
          if (!endpoint && import.meta.env.VITE_SUPABASE_URL) {
            endpoint = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/speechToText`;
          }
          if (!endpoint) {
            console.error(
              '[useGoogleStt] STT endpoint not configured. Set VITE_STT_FUNCTION_URL or VITE_SUPABASE_URL.'
            );
            toast({ title: '오류', description: 'STT 서버 URL이 설정되지 않았습니다.' });
            return;
          }
          // build headers with Supabase anon key for Functions auth
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
          if (anonKey) {
            headers['Authorization'] = `Bearer ${anonKey}`;
            headers['apikey'] = anonKey;
          }
          const res = await fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify({ audioBase64: base64 }),
          });
          // handle no content (204) to avoid JSON parse error
          if (res.status === 204) {
            return;
          }
          if (!res.ok) {
            const errText = await res.text();
            console.error(
              '[useGoogleStt] STT fetch failed',
              res.status,
              res.statusText,
              errText
            );
            toast({ title: '오류', description: `STT 서버 오류 (${res.status})` });
            return;
          }
          const json = await res.json();
          const { transcription } = json as { transcription?: string };
          if (transcription) setText(transcription + ' ');
        } catch (err) {
          if (err instanceof Error) {
            console.error('[useGoogleStt] chunk STT error:', err.message, err.stack);
            toast({ title: '오류', description: '음성 인식 중 오류가 발생했습니다.' });
          } else {
            console.error('[useGoogleStt] chunk STT error (unknown):', err);
          }
        }
      };

      recorder.ondataavailable = (e) => {
        if (e.data.size) transcribeChunk(e.data);
      };

      recorder.onstop = async () => {
        // final chunk already processed, nothing extra
      };

      recorder.start(50); // emit chunks every 0.5s

      // waveform loop
      const data = new Uint8Array(analyser.fftSize);
      const draw = () => {
        analyser.getByteTimeDomainData(data);
        const seg = Math.floor(data.length / 32);
        const bars = Array.from({ length: 32 }, (_, i) => {
          let sum = 0;
          for (let j = 0; j < seg; j++) {
            sum += Math.abs(data[i * seg + j] - 128);
          }
          return sum / seg; // 0~128
        });
        setWaveform(bars);
        animRef.current = requestAnimationFrame(draw);
      };
      draw();
    } catch (err) {
      console.error('getUserMedia 에러', err);
      toast({ title: '오류', description: '마이크 권한이 필요합니다.' });
      setListening(false);
      return;
    }
  };

  const stop = () => {
    if (!listening) return;
    mediaRef.current?.stop();
    animRef.current && cancelAnimationFrame(animRef.current);
    audioCtxRef.current?.close();
    
    setListening(false);
  };

  useEffect(() => {
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { text, listening, start, stop, waveform };
}
